import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Translate, withLocalize } from 'react-localize-redux';
import { isNullOrUndefined } from '../../../store/storeFunctions';
import NoDataComponent from "../../common/noDataComponent";
import BbPopover from '../../common/popover';
import UnderlinedTitle from '../../common/underlinedTitle';
import RatingCard from '../rating/ratingCard';
import EsgTachometer from './esgTachometer';

export const ESG_SCORE_SECTION_TYPE = {
    detail: "ESGScoreDetail",
    scoreE: "ESGScoreE",
    scoreS: "ESGScoreS",
    scoreG: "ESGScoreG"
}

const ESG_SCORE_DISPLAY_MODE = {
    card: "card",
    block: "block"
}

const ESG_SCORE_SETTINGS = {
    [ESG_SCORE_SECTION_TYPE.detail]: {
        translationKey: "Report.ESG.ScoreDetail",
        displaySettings: {
            mode: ESG_SCORE_DISPLAY_MODE.card
        }
    },
    [ESG_SCORE_SECTION_TYPE.scoreE]: {
        translationKey: "Report.ESG.Score",
        translationData: {
            scoreType: "E"
        },
        displaySettings: {
            mode: ESG_SCORE_DISPLAY_MODE.block
        }
    },
    [ESG_SCORE_SECTION_TYPE.scoreS]: {
        translationKey: "Report.ESG.Score",
        translationData: {
            scoreType: "S"
        },
        displaySettings: {
            mode: ESG_SCORE_DISPLAY_MODE.block
        }
    },
    [ESG_SCORE_SECTION_TYPE.scoreG]: {
        translationKey: "Report.ESG.Score",
        translationData: {
            scoreType: "G"
        },
        displaySettings: {
            mode: ESG_SCORE_DISPLAY_MODE.block
        }
    }
}

class ESGScoreStatistics extends React.Component {

    get settings() {
        return ESG_SCORE_SETTINGS[this.props.section];
    }

    render() {
        const { data, score } = this.props;
        return isNullOrUndefined(data) || isNullOrUndefined(score) ? <NoDataComponent /> : this.renderContent();
    }

    renderContent() {
        const { section, data, score, isSector, sectorIdentificator, primaryAttrCountry } = this.props;
        const isDetail = this.props.section === ESG_SCORE_SECTION_TYPE.detail;

        const title = this.props.translate(this.getTranslationKey(isSector, primaryAttrCountry), { sectorId: sectorIdentificator });
        const tooltip = this.getTooltip(isSector, primaryAttrCountry, isDetail);
        const descriptionKey = this.getDescriptionKey(score, isSector, primaryAttrCountry);
        const descriptionData = this.getDescriptionKeyData(data, score, sectorIdentificator);

        switch (this.settings.displaySettings.mode) {
            case ESG_SCORE_DISPLAY_MODE.card: {
                const id = `${section}-${isSector ? "Sector" : "Market"}`;

                return (
                    <RatingCard
                        cardType={id}
                        title={title}
                        tooltip={tooltip}
                    >
                        <div className='esg-card-statistics'>
                            <div><EsgTachometer id={id} data={data} score={score} /></div>
                            <span><Translate id={descriptionKey} data={descriptionData} /></span>
                        </div>
                    </RatingCard>
                );
            }
            case ESG_SCORE_DISPLAY_MODE.block:
                {
                    const id = `${section}-${isSector ? "Sector" : "Market"}`;
                    const popoverId = `header-tooltip-${id}`;
                    return (
                        <div className='esg-block-statistics'>
                            <div className='header'>
                                <UnderlinedTitle header={title} />
                                <span>
                                    <BbPopover
                                        title={title}
                                        content={tooltip}
                                        placement="left"
                                        targetId={popoverId}
                                    >
                                        <span href="#" className="icon-gray print-hidden" id={popoverId} style={{ cursor: "pointer" }}><FontAwesomeIcon icon="question-circle" /></span>
                                    </BbPopover>
                                </span>
                            </div>
                            <div className='content'>
                                <div className='image'><EsgTachometer id={id} data={data} score={score} /></div>
                                <span className='description'><Translate id={descriptionKey} data={descriptionData} /></span>
                            </div>
                        </div>
                    );
                }
            default:
                return null;
        }
    }

    getTranslationKey = (isSector, primaryAttrCountry, subKey = "") => `${this.settings.translationKey}.${isSector ? "Sector" : "Market"}${subKey}${isSector ? "" : `.${primaryAttrCountry}`}`;

    getTooltipKey = (isSector, primaryAttrCountry, isDetail, subKey = "") => `${this.settings.translationKey}.${isSector ? "Sector" : "Market"}${subKey}${isSector && !isDetail ? "" : `.${primaryAttrCountry}`}`;

    getTooltip = (isSector, primaryAttrCountry, isDetail) => ReactHtmlParser(this.props.translate(this.getTooltipKey(isSector, primaryAttrCountry, isDetail, ".Tooltip"), this.settings.translationData));

    getDescriptionKey = (score, isSector, primaryAttrCountry) => this.getTranslationKey(isSector, primaryAttrCountry, `.Description.${score}`);

    getDescriptionKeyData(data, score, sectorIdentificator) {
        let result = ({
            ...this.settings.translationData,
            n_avg: this.formatValue(data.n_avg, 2),
            sectorId: sectorIdentificator
        });

        switch (score) {
            case 1:
                result = ({ ...result, n_1: this.formatValue(data.n_1) });
                break;
            case 2:
                result = ({ ...result, n_2: this.formatValue(data.n_2) });
                break;
            case 3:
                result = ({ ...result, n_3: this.formatValue(data.n_3) });
                break;
            case 4:
                result = ({ ...result, n_4: this.formatValue(data.n_4) });
                break;
            case 5:
                result = ({ ...result, n_5: this.formatValue(data.n_5) });
                break;
            default:
                break;
        }

        return result;
    }

    formatValue(value, defaultDigits = undefined) {
        if (value) {
            const digits = defaultDigits ? defaultDigits : value < 1 ? 1 : 0;
            return Intl.NumberFormat(this.props.activeLanguage.code, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
        }
        return value;
    }
}

ESGScoreStatistics.propTypes = {
    section: PropTypes.oneOf([ESG_SCORE_SECTION_TYPE.detail, ESG_SCORE_SECTION_TYPE.scoreE, ESG_SCORE_SECTION_TYPE.scoreS, ESG_SCORE_SECTION_TYPE.scoreG]).isRequired,
    data: PropTypes.shape({
        n_avg: PropTypes.number,
        n_1: PropTypes.number,
        n_2: PropTypes.number,
        n_3: PropTypes.number,
        n_4: PropTypes.number,
        n_5: PropTypes.number,
    }).isRequired,
    score: PropTypes.oneOf([1, 2, 3, 4, 5]).isRequired,
    isSector: PropTypes.bool.isRequired,
    sectorIdentificator: PropTypes.string,
    primaryAttrCountry: PropTypes.oneOf(["CZ", "SK"])
};

ESGScoreStatistics.defaultProps = {
    section: undefined,
    data: null,
    score: undefined,
    isSector: false,
    sectorIdentificator: "",
    primaryAttrCountry: "CZ"
};

export default withLocalize(ESGScoreStatistics);